// src/modules/app-views/goodsReceipt/pages/Document/tabs/RecordTab/CarrierCard.jsx
// *******************************************************************************
// CARRIER CARD (TRANSPORTADORA)
// *******************************************************************************
// Card for selecting carrier entity and displaying transport/logistics details.
// Includes: carrier entity picker, freight mode, vehicle plate, volumes/weights.
// Saves to form fields: logistics.carrier.entityId, logistics.freightModeCode,
// logistics.vehicle.plateNumber, logistics.volumes.* etc.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Row, Col, Descriptions, Divider } from 'antd';
import SimpleSelectAsyncEntities from 'components/SimpleComponents/SimpleSelectAsyncEntities';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { openModal } from 'components/core/GenericModal';
import EditEntityModal from 'modules/app-views/customers/components/EditEntityModal';
import translateX from 'utils/translateX';
import { parsePhoneToDisplay } from 'utils/helpers';

// Entity profiles for carrier search
const CARRIER_PROFILES = ['carrier', 'company'];

// Freight mode options (SEFAZ modFrete codes stored as strings)
const FREIGHT_MODE_OPTIONS = [
  { value: '0', label: 'freight_sender' },
  { value: '1', label: 'freight_recipient' },
  { value: '2', label: 'freight_third_party' },
  { value: '3', label: 'freight_own_sender' },
  { value: '4', label: 'freight_own_recipient' },
  { value: '9', label: 'freight_no_freight' },
];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CarrierCard = ({ data, viewMode, onActionsChange }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { form } = useSimpleFormContext() || {};
  const { refresh } = useDocumentContext() || {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isLocked = viewMode === 'view';
  const isConfirmed = data?.status === 'confirmed';

  // Build translated freight mode options
  const freightModeOptions = useMemo(
    () => FREIGHT_MODE_OPTIONS.map(opt => ({ value: opt.value, label: translateX(opt.label) })),
    [],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const initRef = useRef(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // Initialize carrier from hydrated entity or saved snapshot
  useEffect(() => {
    if (initRef.current || !data?._id) return;

    const carrierEntity = data?.carrierEntity;
    const carrierSnapshot = data?.logistics?.carrier;

    if (carrierEntity?._id) {
      initRef.current = true;
      setSelectedCarrier(carrierEntity);
      form?.setFieldsValue({ carrierId: carrierEntity._id });
    } else if (carrierSnapshot?.entityId) {
      initRef.current = true;
      // Build a minimal entity object from snapshot for display
      setSelectedCarrier({
        _id: carrierSnapshot.entityId,
        name: carrierSnapshot.name,
        uniqueKey: carrierSnapshot.cpfCnpj,
      });
      form?.setFieldsValue({ carrierId: carrierSnapshot.entityId });
    }
  }, [data, form]);

  // Expose actions to parent for Collapse header buttons
  useEffect(() => {
    onActionsChange?.({
      handleNewCarrier: () => handleNewEntity(),
      handleEditCarrier: () => handleEditEntity(),
      selectedCarrier,
    });
  }, [onActionsChange, selectedCarrier]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleEntitySelect = useCallback(
    entity => {
      setSelectedCarrier(entity);

      if (!entity) {
        form?.setFieldsValue({
          carrierId: undefined,
          carrier: undefined,
        });
        return;
      }

      // Extract CNPJ/CPF
      const cnpjDoc = entity?.documents?.find?.(d => d.documentType === 'cnpj');
      const cpfDoc = entity?.documents?.find?.(d => d.documentType === 'cpf');
      const cpfCnpj = cnpjDoc?.value || cpfDoc?.value || entity?.uniqueKey || '';

      // Extract phone
      const phoneContact = entity?.contacts?.find?.(c => c.contactType === 'phone');
      const phone = phoneContact?.value || '';

      // Extract IE
      const ie = entity?.fiscalData?.stateRegistration || entity?.ie || '';

      // Extract address
      const addr = entity?.address || entity?.addresses?.[0] || {};

      form?.setFieldsValue({
        carrierId: entity._id,
        carrier: {
          entityId: entity._id,
          cpfCnpj,
          name: entity.razaoSocial || entity.name,
          tradeName: entity.nomeFantasia || entity.tradeName || '',
          stateRegistration: ie,
          phone,
          address: {
            address1: addr.address1 || addr.street || '',
            number: addr.number || '',
            neighborhood: addr.neighborhood || '',
            city: addr.city || '',
            region: addr.region || addr.state || '',
            zip: addr.zip || addr.zipCode || '',
            countryCode: addr.countryCode || 'BR',
          },
        },
      });
    },
    [form],
  );

  const handleNewEntity = useCallback(() => {
    openModal(EditEntityModal, {
      entityProfiles: CARRIER_PROFILES,
      afterSave: entity => {
        if (entity) handleEntitySelect(entity);
      },
    });
  }, [handleEntitySelect]);

  const handleEditEntity = useCallback(() => {
    if (!selectedCarrier?._id) return;
    openModal(EditEntityModal, {
      entityId: selectedCarrier._id,
      entityProfiles: CARRIER_PROFILES,
      afterSave: entity => {
        if (entity) {
          handleEntitySelect(entity);
          refresh?.();
        }
      },
    });
  }, [selectedCarrier, handleEntitySelect, refresh]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const carrierInfo = useMemo(() => {
    if (!selectedCarrier) return null;

    const snapshot = data?.logistics?.carrier || {};
    const entity = selectedCarrier;

    return {
      name: entity.razaoSocial || entity.name || snapshot.name || '',
      tradeName: entity.nomeFantasia || entity.tradeName || snapshot.tradeName || '',
      cpfCnpj: entity.uniqueKey || snapshot.cpfCnpj || '',
      ie: entity.fiscalData?.stateRegistration || entity.ie || snapshot.stateRegistration || '',
      phone: parsePhoneToDisplay(
        entity.contacts?.find?.(c => c.contactType === 'phone')?.value ||
        snapshot.phone || '',
      ),
      address: buildAddressString(entity.address || entity.addresses?.[0] || snapshot.address),
    };
  }, [selectedCarrier, data?.logistics?.carrier]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Carrier entity picker */}
      <Row gutter={[16, 0]}>
        <Col xs={24} md={12}>
          <SimpleSelectAsyncEntities
            name="carrierId"
            label="search_carrier"
            entityProfiles={CARRIER_PROFILES}
            disabled={isLocked || isConfirmed}
            onEntitySelect={handleEntitySelect}
            selectedEntity={selectedCarrier}
          />
        </Col>

        <Col xs={24} md={12}>
          <SimpleSelect
            name={['logistics', 'freightModeCode']}
            label="freight_mode"
            options={freightModeOptions}
            disabled={isLocked || isConfirmed}
          />
        </Col>
      </Row>

      {/* Carrier info display */}
      {carrierInfo && (
        <>
          <Divider style={{ margin: '8px 0 12px' }} />
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
            <Descriptions.Item label={translateX('name')}>
              {carrierInfo.name}
            </Descriptions.Item>
            {carrierInfo.tradeName && (
              <Descriptions.Item label={translateX('trade_name')}>
                {carrierInfo.tradeName}
              </Descriptions.Item>
            )}
            {carrierInfo.cpfCnpj && (
              <Descriptions.Item label="CNPJ/CPF">
                {carrierInfo.cpfCnpj}
              </Descriptions.Item>
            )}
            {carrierInfo.ie && (
              <Descriptions.Item label="IE">
                {carrierInfo.ie}
              </Descriptions.Item>
            )}
            {carrierInfo.phone && (
              <Descriptions.Item label={translateX('phone')}>
                {carrierInfo.phone}
              </Descriptions.Item>
            )}
            {carrierInfo.address && (
              <Descriptions.Item label={translateX('address')} span={3}>
                {carrierInfo.address}
              </Descriptions.Item>
            )}
          </Descriptions>
        </>
      )}

      {/* Vehicle and Volumes */}
      <Divider style={{ margin: '12px 0' }} />
      <Row gutter={[16, 0]}>
        <Col xs={24} md={8}>
          <SimpleInput
            name={['logistics', 'vehicle', 'plateNumber']}
            label="vehicle_plate"
            disabled={isLocked || isConfirmed}
            placeholder="ABC-1234"
          />
        </Col>
        <Col xs={24} md={4}>
          <SimpleInput
            name={['logistics', 'vehicle', 'registrationState']}
            label="registration_state"
            disabled={isLocked || isConfirmed}
            placeholder="SP"
            maxLength={2}
          />
        </Col>
        <Col xs={24} md={4}>
          <SimpleNumber
            name={['logistics', 'volumes', 'quantity']}
            label="volumes_quantity"
            disabled={isLocked || isConfirmed}
            min={0}
          />
        </Col>
        <Col xs={24} md={4}>
          <SimpleNumber
            name={['logistics', 'volumes', 'grossWeight']}
            label="gross_weight"
            disabled={isLocked || isConfirmed}
            min={0}
            decimalSeparator=","
            precision={3}
          />
        </Col>
        <Col xs={24} md={4}>
          <SimpleNumber
            name={['logistics', 'volumes', 'netWeight']}
            label="net_weight"
            disabled={isLocked || isConfirmed}
            min={0}
            decimalSeparator=","
            precision={3}
          />
        </Col>
      </Row>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CarrierCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Build a display string from address object
 */
function buildAddressString(addr) {
  if (!addr) return '';
  const parts = [
    addr.address1 || addr.street,
    addr.number,
    addr.neighborhood,
    addr.city,
    addr.region || addr.state,
  ].filter(Boolean);
  return parts.join(', ');
}
